import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import '../Championship.css';

const API_KEY      = import.meta.env.VITE_API_KEY || '';
const FIRST_YEAR   = 2024;
const CURRENT_YEAR = new Date().getFullYear();

const CHAMP_OPTIONS = [
  { key: 'TT',   label: 'Top Ten Championship' },
  { key: 'open', label: 'Open Class Championship' },
  { key: 'FE',   label: 'Forgotten Era Championships' },
  { key: 'VC',   label: 'Vintage/Classic Championship' },
  { key: 'RL',   label: 'Road Legal Championship' },
];
const VALID_CHAMPS = new Set(CHAMP_OPTIONS.map(o => o.key));

function clampSeason(raw) {
  const n = parseInt(raw, 10);
  if (!n || n < FIRST_YEAR) return CURRENT_YEAR;
  if (n > CURRENT_YEAR) return CURRENT_YEAR;
  return n;
}

function fmtFooterDate() {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

export default function OpenChampionship() {
  const [searchParams, setSearchParams] = useSearchParams();
  const season   = clampSeason(searchParams.get('season'));
  const rawClass = searchParams.get('class');
  const champ    = rawClass === 'FEO' ? 'FE' : (VALID_CHAMPS.has(rawClass) ? rawClass : 'open');

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  function setParam(key, val) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(key, val);
      return next;
    });
  }

  useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    fetch(`/api/results/championship?season=${season}&class=${champ}`, {
      headers: { 'X-API-Key': API_KEY },
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [season, champ]);

  const years = [];
  for (let y = CURRENT_YEAR; y >= FIRST_YEAR; y--) years.push(y);

  const subtitle = data?.label ?? CHAMP_OPTIONS.find(o => o.key === champ)?.label ?? '';

  return (
    <div className="championship-report" data-proof-ready={!loading && data ? 'true' : undefined}>

      {/* ── Header photo ─────────────────────────────────────────────────── */}
      <img
        src="/championship/header.jpg"
        alt="NHCA hillclimb"
        className="cr-header-photo"
      />

      {/* ── Title section ────────────────────────────────────────────────── */}
      <div className="cr-title-section">
        <img src="/championship/coffee.jpg" alt="" className="cr-coffee-img" />
        <div className="cr-title-text">
          <select
            className="cr-season-select"
            value={season}
            onChange={e => setParam('season', e.target.value)}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <h1>NHCA {season}</h1>
          <p>{subtitle}</p>
        </div>
        <img src="/championship/coffee_stain.png" alt="" className="cr-coffee-stain" />
      </div>

      {/* ── Championship type selector ───────────────────────────────────── */}
      <div className="cr-champ-tabs">
        {CHAMP_OPTIONS.map(o => (
          <button
            key={o.key}
            className={`cr-tab${champ === o.key ? ' cr-tab-active' : ''}`}
            onClick={() => setParam('class', o.key)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      {loading && <div className="cr-loading">Loading results…</div>}
      {error   && <div className="cr-error">⚠ {error}</div>}

      {data?.warnings?.length > 0 && (
        <div className="cr-warning">
          {data.warnings.map((w, i) => (
            <p key={i}>⚠ {w.message}</p>
          ))}
        </div>
      )}

      {data && (() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const seasonComplete = data.events.length > 0 &&
          data.events.every(e => new Date(e.date) < today);
        return data.classes.map(cls => (
          <ClassSection
            key={cls.name}
            cls={cls}
            events={data.events}
            bestN={data.best_n}
            champ={champ}
            seasonComplete={seasonComplete}
          />
        ));
      })()}

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div className="cr-footer">{fmtFooterDate()}</div>
    </div>
  );
}

const CLASS_DISPLAY_NAMES = { FEO: 'FE Overall' };

const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

function RiderName({ name }) {
  const andIdx = name.indexOf(' and ');
  if (andIdx === -1) return <>{name}</>;
  return (
    <>
      {name.slice(0, andIdx)}
      <span className="cr-rider-pax">and {name.slice(andIdx + 5)}</span>
    </>
  );
}

function ClassSection({ cls, events, bestN, champ, seasonComplete }) {
  const isTT = champ === 'TT';
  const displayName = CLASS_DISPLAY_NAMES[cls.name] ?? cls.name;
  const totalCols = 5 + events.length + 1; // rank + rider + best_n + total + scores + events + tiebrk

  return (
    <div className="cr-class-section">
      <table className="cr-table">
        <thead>
          {/* Row 1: rotated column headers */}
          <tr className="cr-col-headers">
            <th className="cr-th-rank" />
            <th className="cr-th-rider" />
            <th className="cr-th-fixed">
              <span className="cr-rotated">Best {bestN}</span>
            </th>
            <th className="cr-th-fixed">
              <span className="cr-rotated">Total</span>
            </th>
            <th className="cr-th-fixed">
              <span className="cr-rotated">Scores</span>
            </th>
            {events.map(e => (
              <th key={e.key} className="cr-th-event">
                {champ === 'VC' || champ === 'open' || champ === 'TT' || champ === 'RL' || champ === 'FE'
                  ? <Link className="cr-rotated cr-event-link" to={`/results?hill=${encodeURIComponent(e.hill)}&date=${e.date}${champ === 'VC' ? '&vc=true' : champ === 'RL' ? '&rl=true' : champ === 'FE' ? '&fe=true' : ''}`}>{e.hill}</Link>
                  : <span className="cr-rotated">{e.hill}</span>}
              </th>
            ))}
            <th className="cr-th-tiebrk">
              <span className="cr-rotated">Tie brk</span>
            </th>
          </tr>

          {/* Row 2: class name band */}
          <tr className="cr-class-name-row">
            <td colSpan={totalCols}>
              <div className="cr-class-name-cell">
                <span className="cr-class-square" />
                <span className="cr-class-label">{displayName}</span>
              </div>
            </td>
          </tr>
        </thead>

        <tbody>
          {cls.riders.map(rider => {
            const medal = (!isTT && seasonComplete) ? (MEDALS[rider.rank] ?? null) : null;
            const isTopTen = isTT && seasonComplete && rider.rank <= 10;
            return (
              <tr key={rider.rider} className={isTopTen ? 'cr-top-ten-row' : ''}>
                <td className="cr-td-rank">{rider.rank}</td>
                <td className="cr-td-rider">
                  <div className="cr-rider-cell">
                    <span className="cr-medal-slot">{medal ?? ''}</span>
                    <RiderName name={rider.rider} />
                  </div>
                </td>
                <td>{rider.best_n ?? ''}</td>
                <td>{rider.total  ?? ''}</td>
                <td>{rider.num_scores ?? ''}</td>
                {events.map(e => (
                  <td key={e.key}>
                    {rider.event_scores[e.key] ?? ''}
                  </td>
                ))}
                <td className="cr-td-tiebrk">
                  {rider.tie_break ? `{${rider.tie_break.join(',')}}` : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
